import { type Schemas, api } from '@/api/client'
import { useAuthStore } from '@/stores/auth.store'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

type Workspace = Schemas['Workspace']

type WorkspaceContextValue = {
  current: Workspace | null
  available: Workspace[]
  switchTo: (code: string) => void
  isLoading: boolean
  // PERF audit Lot 3.4 (2026-07-09): surfaced so HomeRedirect can show a
  // retry action instead of an indefinite skeleton when the workspaces
  // fetch fails (timeout, backend error) rather than just being slow.
  isError: boolean
  retry: () => void
}

const STORAGE_KEY = 'amanet_workspace'

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

function readStored(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStored(code: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, code)
  } catch {
    // localStorage may be unavailable (private mode) — silent fallback.
  }
}

function pickByPriority(list: Workspace[]): Workspace | null {
  if (!list.length) return null
  // default_priority is "weight": higher = more preferred (admin=100, finance=80…).
  return [...list].sort((a, b) => b.default_priority - a.default_priority)[0] ?? null
}

function resolveCurrentFromPath(
  pathname: string,
  list: Workspace[],
): Workspace | null {
  // Pick the workspace whose home_path or any primary_modules.path is the
  // longest prefix of the current location. Lets the switcher follow deep links.
  let best: Workspace | null = null
  let bestLen = -1
  for (const ws of list) {
    const candidates = [ws.home_path, ...ws.primary_modules.map((m) => m.path)]
    for (const path of candidates) {
      if (!path) continue
      if (pathname === path || pathname.startsWith(`${path}/`)) {
        if (path.length > bestLen) {
          best = ws
          bestLen = path.length
        }
      }
    }
  }
  return best
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const userId = useAuthStore((s) => s.user?.id)
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const [storedCode, setStoredCode] = useState<string | null>(() => readStored())

  // PERF audit Lot 3.5 (2026-07-09): auth-critical queries — pause instead
  // of firing a doomed fetch while genuinely offline (the global default
  // in lib/query.ts is 'offlineFirst', tuned for cached list views).
  const workspacesQuery = useQuery({
    queryKey: ['workspaces', 'mine', userId],
    queryFn: () => api.workspaces.listMine(),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
    networkMode: 'online',
  })

  const preferencesQuery = useQuery({
    queryKey: ['my', 'preferences', userId],
    queryFn: () => api.my.getPreferences(),
    enabled: isAuthenticated,
    staleTime: 10 * 60_000,
    retry: 1,
    networkMode: 'online',
  })

  const available = useMemo<Workspace[]>(
    () => workspacesQuery.data ?? [],
    [workspacesQuery.data],
  )

  const current = useMemo<Workspace | null>(() => {
    if (!available.length) return null

    // 1. Follow the URL: deep links override stored selection.
    const fromPath = resolveCurrentFromPath(pathname, available)
    if (fromPath) return fromPath

    // 2. Stored selection if still valid.
    if (storedCode) {
      const stored = available.find((w) => w.code === storedCode)
      if (stored) return stored
    }

    // 3. User's preferred default.
    const preferred = preferencesQuery.data?.default_workspace ?? null
    if (preferred) {
      const fromPref = available.find((w) => w.code === preferred)
      if (fromPref) return fromPref
    }

    // 4. Highest-priority accessible workspace.
    return pickByPriority(available)
  }, [available, pathname, storedCode, preferencesQuery.data])

  // Keep storage in sync with whatever the URL settled on.
  useEffect(() => {
    if (current && current.code !== storedCode) {
      setStoredCode(current.code)
      writeStored(current.code)
    }
  }, [current, storedCode])

  const switchTo = useCallback(
    (code: string) => {
      const ws = available.find((w) => w.code === code)
      if (!ws) return
      setStoredCode(ws.code)
      writeStored(ws.code)
      void navigate({ to: ws.home_path })
    },
    [available, navigate],
  )

  const retry = useCallback(() => {
    void workspacesQuery.refetch()
    void preferencesQuery.refetch()
  }, [workspacesQuery, preferencesQuery])

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      current,
      available,
      switchTo,
      isLoading: workspacesQuery.isLoading,
      isError: workspacesQuery.isError,
      retry,
    }),
    [current, available, switchTo, workspacesQuery.isLoading, workspacesQuery.isError, retry],
  )

  return (
    <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
  )
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) {
    throw new Error('useWorkspace must be used within <WorkspaceProvider>')
  }
  return ctx
}
